import { Link, useParams } from "react-router-dom";
import Seo from "@/components/site/Seo";
import SearchBar from "@/components/site/SearchBar";
import BrandCard from "@/components/site/BrandCard";
import ConversionCTA from "@/components/site/ConversionCTA";
import CategoryFaq from "@/components/site/CategoryFaq";
import { getCategoryCopy } from "@/lib/categoryCopy";
import { useBrands, useCategories } from "@/lib/brands";

export default function CategoryPage() {
  const { slug } = useParams();
  const { data: brands = [], isLoading } = useBrands({ category: slug });
  const { data: categories = [] } = useCategories();
  const cat = categories.find((c) => c.slug === slug);
  const title = cat?.name || slug;
  const copy = getCategoryCopy(slug);

  const jsonLd = copy?.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: copy.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : undefined;

  return (
    <div className="pb-16">
      <Seo
        title={`${title} Customer Service Numbers — It's Genie Assist`}
        description={`Verified official customer service contacts for ${title} brands. Independent directory, not affiliated with any brand listed.`}
        jsonLd={jsonLd}
      />

      <section className="border-b border-hair py-12 sm:py-16">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">Category</p>
        <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
          {title}
        </h1>
        {copy?.hero ? (
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink" data-testid="category-hero-tagline">
            {copy.hero}
          </p>
        ) : (
          cat?.blurb && <p className="mt-3 max-w-2xl text-muted">{cat.blurb}</p>
        )}
        <div className="mt-8 max-w-xl">
          <SearchBar autoFocus={false} size="compact" />
        </div>
      </section>

      <section className="py-10">
        {isLoading ? (
          <div className="font-mono text-sm text-muted">Loading…</div>
        ) : brands.length === 0 ? (
          <div className="border border-hair p-8 text-center">
            <p className="font-serif text-xl text-ink">No brands here yet.</p>
            <Link to="/contact" className="mt-3 inline-block border-b border-ink font-mono text-xs uppercase tracking-widest hover:text-accent">
              Request a brand →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-px bg-hair sm:grid-cols-3 lg:grid-cols-4">
            {brands.map((b, i) => (
              <div key={b.slug} className="bg-paper">
                <BrandCard brand={b} index={i} />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="border border-hair p-6">
        <p className="font-mono text-[11px] leading-relaxed tracking-wide text-muted">
          It&apos;s Genie Assist is independent and not affiliated with any brand listed. Contacts
          are sourced from official brand websites.
        </p>
      </section>

      {copy?.faqs?.length > 0 && <CategoryFaq title={title} faqs={copy.faqs} />}

      <ConversionCTA
        heading={copy?.ctaHeading || `Can't find your ${title.toLowerCase()} issue? A specialist can help.`}
        sub={copy?.ctaSub}
        ctaHref={`/chat?category=${slug}`}
      />
    </div>
  );
}
